/**
 * ReferencePersonPickerDialog Component
 *
 * Dialog for selecting a reference person from the AI Avatar feature.
 * Integrates with existing referencePersons data.
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

interface IReferencePersonPickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (personId: Id<'referencePersons'>) => void;
}

interface IReferencePerson {
  _id: Id<'referencePersons'>;
  name: string;
  description?: string;
  imageUrls?: string[];
}

export function ReferencePersonPickerDialog({
  isOpen,
  onClose,
  onSelect,
}: IReferencePersonPickerDialogProps) {
  // Fetch reference persons
  const personsData = useQuery(api.referencePersons.getActiveReferencePersons);
  const persons: IReferencePerson[] = (personsData ?? []) as IReferencePerson[];
  const isLoading = personsData === undefined;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="size-5" />
            Select Reference Person
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
          ) : persons.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <User className="mb-3 size-12 text-muted-foreground/50" />
              <p className="text-muted-foreground">No reference persons found</p>
              <p className="mt-1 text-sm text-muted-foreground/70">
                Create a reference person in the AI Avatar section first
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 p-1">
              {persons.map(person => (
                <Card
                  key={person._id}
                  className={cn(
                    'group cursor-pointer overflow-hidden transition-all hover:ring-2 hover:ring-primary'
                  )}
                  onClick={() => onSelect(person._id)}
                >
                  <div className="aspect-square overflow-hidden bg-muted">
                    {person.imageUrls && person.imageUrls.length > 0 ? (
                      <img
                        src={person.imageUrls[0]}
                        alt={person.name}
                        className="size-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex size-full items-center justify-center">
                        <User className="size-12 text-muted-foreground/50" />
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="truncate text-sm font-medium">{person.name}</p>
                    {person.description && (
                      <p className="truncate text-xs text-muted-foreground">{person.description}</p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
